// ink-component
import React, { FocusEvent, ChangeEvent, useEffect, useRef, useState } from 'react';
import isNil from 'lodash/isNil';

import Control from '../Control/Control';
import CustomText from '../Typography/CustomText/CustomText';

import { bemHelper } from '../../helpers';
import setFocus from '../../helpers/dom/setFocus';
import useDidUpdateEffect from '../../hooks/useDidUpdateEffect';
import useWindowWidth from '../../hooks/useWindowWidth';

import useDerecatedWarning from '../../hooks/useDeprecatedWarning';
import getGlobalProps from '../../helpers/constants/global-props';

type Meta = {
  initial?: string;
  isInvalid?: boolean;
};

type Input = {
  value?: string;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
};

export interface TextAreaProps {
  /** Unique identifier */
  id: string;
  /** Unique name */
  name?: string;
  /** Number of rows */
  rows?: number;
  /** External control and initial value */
  value?: string;
  /** Placeholder for when values are empty */
  placeholder?: string;
  /** Sets max text length and display character counter  */
  maxLength?: number;
  /** Size modifier */
  size?: 'full' | 'large';
  /** Modifier for in Table */
  isCell?: boolean;
  /** Modifier for not valid state */
  isInvalid?: any;
  /** Modifier for disabled state */
  isDisabled?: boolean;
  /** Modifier to disable resizable */
  isUnresizable?: boolean;
  /** Hides characters counter */
  hideCounter?: boolean;
  /** onFocus callback. Passes the Event */
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  /** onChange callback. Passes the Event */
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  /** onBlur callback. Passes the Event */
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  /** HTML type */
  autoFocus?: boolean;
  /** Allows screen readers to describe the content. The correct use of Field excludes the need for aria-label */
  'aria-label'?: string;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;

  /** @ignore */
  dataPrivate?: boolean;
  /** @ignore */
  defaultValue?: string;
  /** @ignore */
  meta?: Meta;
  /** @ignore */
  input?: Input;
}

const globalProps = getGlobalProps({ deprecated: true });

/**
 * @purpose Allows users to insert long strings of data
 * @categories form
 * @deprecated This component is deprecated and replaced by [NewTextArea](https://ink.carta.com/components/NewTextArea/)
 * @status deprecated
 * @replacementComponent NewTextArea
 * @dateOfDeletion December 1st, 2023
 */
function TextArea({ input = {}, meta = {}, ...props }: TextAreaProps) {
  useDerecatedWarning({ componentName: 'TextArea', substitute: 'NewTextArea' });

  const {
    id,
    size,
    isCell,
    isDisabled,
    hideCounter,
    name,
    rows,
    maxLength,
    autoFocus,
    placeholder,
    'aria-label': ariaLabel,
    'data-testid': dataTestId,
  } = props;

  const { isMobile } = useWindowWidth();

  const initialValue: string | undefined = input.value || props.value || meta.initial || props.defaultValue;
  const [value, setValue] = useState(initialValue);

  const isInvalid = Boolean(meta.isInvalid || props.isInvalid);

  const shouldShowCounter = maxLength && !hideCounter && !isCell;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useDidUpdateEffect(() => {
    if (!isNil(input.value)) {
      setValue(input.value);
    } else if (!isNil(props.value)) {
      setValue(props.value);
    }
  }, [input.value || props.value]);

  useEffect(() => {
    if (props.autoFocus) setFocus(textareaRef);
  }, [props.autoFocus]);

  const defineClassName = (): string => {
    const { isCell, isUnresizable } = props;
    const b = bemHelper();

    return b('ink-control--textarea', {
      unresizable: isUnresizable || isCell,
      cell: isCell,
    });
  };

  const defineCountClassName = (): string => {
    const { size } = props;
    const responsiveSize = isMobile ? 'full' : size;

    const b = bemHelper();

    return b('ink-control--textarea--count', {
      [`${responsiveSize}`]: Boolean(responsiveSize) && size !== 'large',
    });
  };

  const handleFocus = e => {
    const { onFocus } = props;
    if (input.onFocus) {
      input.onFocus(e);
    }
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleChange = e => {
    const { onChange } = props;
    const { value } = e.target;

    if (input.onChange) {
      input.onChange(e);
    }
    if (onChange) {
      onChange(e);
    }
    setValue(value);
  };

  const handleBlur = e => {
    if (input.onBlur) {
      input.onBlur(e);
    }
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  return (
    <>
      <Control
        inputId={id}
        size={size}
        isCell={isCell}
        isInvalid={isInvalid}
        isDisabled={isDisabled}
        dataPrivate={props.dataPrivate}
        data-testid={dataTestId}
      >
        <textarea
          id={id}
          ref={textareaRef}
          name={name}
          aria-label={ariaLabel}
          rows={rows}
          value={value}
          className={defineClassName()}
          disabled={isDisabled}
          onFocus={handleFocus}
          onChange={handleChange}
          onBlur={handleBlur}
          maxLength={maxLength}
          autoComplete="off"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          placeholder={placeholder}
          {...globalProps}
        />
      </Control>
      {shouldShowCounter && (
        <div className={defineCountClassName()}>
          <CustomText size="small" align="right">
            {value?.length} of {maxLength} characters
          </CustomText>
        </div>
      )}
    </>
  );
}

TextArea.defaultProps = {
  name: undefined,
  rows: 4,
  value: '',
  placeholder: ' ',
  maxLength: undefined,
  size: 'large',
  isCell: false,
  isInvalid: false,
  isDisabled: false,
  isUnresizable: false,
  hideCounter: false,
  onFocus: undefined,
  onChange: undefined,
  onBlur: undefined,
  autoFocus: false,

  defaultValue: '',
};

TextArea.displayName = 'TextArea';

export default TextArea;
